import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Upload, X, FileText } from "lucide-react";
import { toast } from "sonner";

function EventoForm({ evento, onSave, onCancel, onUploadDocuments }) {
  const [formData, setFormData] = useState({
    fecha_evento: "",
    titulo_evento: "",
    tipo_evento: "correo",
    personas_concurrentes: "",
    tipificacion: "incumplimiento de pago",
    relato_usuario: ""
  });
  
  const [selectedFiles, setSelectedFiles] = useState([]);

  useEffect(() => {
    if (evento) {
      setFormData({
        fecha_evento: evento.fecha_evento.split('T')[0],
        titulo_evento: evento.titulo_evento,
        tipo_evento: evento.tipo_evento,
        personas_concurrentes: evento.personas_concurrentes,
        tipificacion: evento.tipificacion,
        relato_usuario: evento.relato_usuario
      });
    }
  }, [evento]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Validar máximo 3 archivos
    if (selectedFiles.length + files.length > 3) {
      toast.error('Máximo 3 documentos por evento');
      return;
    }
    
    // Validar tamaño (10MB cada uno)
    const oversizedFiles = files.filter(f => f.size > 10 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      toast.error('Los archivos no deben superar 10MB cada uno');
      return;
    }
    
    setSelectedFiles([...selectedFiles, ...files]);
  };
  
  const removeFile = (index) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      fecha_evento: new Date(formData.fecha_evento).toISOString()
    }, selectedFiles);
  };

  return (
    <Card data-testid="evento-form">
      <CardHeader>
        <CardTitle>{evento ? 'Editar Hecho' : 'Nuevo Hecho'}</CardTitle>
        <CardDescription>Registra los eventos y hechos del caso</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="fecha">Fecha del Evento *</Label>
            <Input
              id="fecha"
              type="date"
              data-testid="evento-fecha-input"
              value={formData.fecha_evento}
              onChange={(e) => setFormData({...formData, fecha_evento: e.target.value})}
              required
            />
          </div>

          <div>
            <Label htmlFor="titulo">Título del Evento *</Label>
            <Input
              id="titulo"
              data-testid="evento-titulo-input"
              value={formData.titulo_evento}
              onChange={(e) => setFormData({...formData, titulo_evento: e.target.value})}
              placeholder="Ej: Correo de reclamo de pago"
              required
            />
          </div>

          <div>
            <Label>Tipo de Evento</Label>
            <Select value={formData.tipo_evento} onValueChange={(value) => setFormData({...formData, tipo_evento: value})}>
              <SelectTrigger data-testid="evento-tipo-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="correo">Correo</SelectItem>
                <SelectItem value="reunion">Reunión</SelectItem>
                <SelectItem value="llamada">Llamada</SelectItem>
                <SelectItem value="pago_incompleto">Pago Incompleto</SelectItem>
                <SelectItem value="acuerdo_incumplido">Acuerdo Incumplido</SelectItem>
                <SelectItem value="otro">Otro</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="personas">Personas Concurrentes</Label>
            <Input
              id="personas"
              data-testid="evento-personas-input"
              value={formData.personas_concurrentes}
              onChange={(e) => setFormData({...formData, personas_concurrentes: e.target.value})}
              placeholder="Nombres y cargos de las personas involucradas"
            />
          </div>

          <div>
            <Label htmlFor="tipificacion">Tipificación</Label>
            <Input
              id="tipificacion"
              data-testid="evento-tipificacion-input"
              value={formData.tipificacion}
              onChange={(e) => setFormData({...formData, tipificacion: e.target.value})}
              placeholder="Ej: incumplimiento de pago, retraso, término unilateral"
            />
          </div>

          <div>
            <Label htmlFor="relato">Relato / Historia *</Label>
            <Textarea
              id="relato"
              data-testid="evento-relato-textarea"
              value={formData.relato_usuario}
              onChange={(e) => setFormData({...formData, relato_usuario: e.target.value})}
              placeholder="Describe detalladamente lo que sucedió en este evento..."
              rows={8}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Tip: Puedes pegar el contenido completo de un correo o documento aquí
            </p>
          </div>

          {/* Subir documentos */}
          <div>
            <Label>Documentos Adjuntos (Opcional - Máximo 3)</Label>
            <div className="mt-2 space-y-2">
              {/* Input oculto */}
              <input
                id="file-upload"
                type="file"
                multiple
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={handleFileChange}
                className="hidden"
                data-testid="file-upload-input"
              />
              
              {/* Botón de subida */}
              {selectedFiles.length < 3 && (
                <label 
                  htmlFor="file-upload"
                  className="flex items-center justify-center w-full p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
                >
                  <div className="text-center">
                    <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">
                      Haz clic para subir PDF o imágenes
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {3 - selectedFiles.length} archivo(s) disponible(s)
                    </p>
                  </div>
                </label>
              )}
              
              {/* Lista de archivos seleccionados */}
              {selectedFiles.length > 0 && (
                <div className="space-y-2">
                  {selectedFiles.map((file, index) => (
                    <div 
                      key={index} 
                      className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg"
                      data-testid={`selected-file-${index}`}
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <FileText className="h-5 w-5 text-blue-600 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {file.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFile(index)}
                        data-testid={`remove-file-${index}`}
                      >
                        <X className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Los documentos se vincularán automáticamente a este hecho
            </p>
          </div>

          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
              Cancelar
            </Button>
            <Button type="submit" className="btn-primary flex-1" data-testid="save-evento-btn">
              {evento ? 'Actualizar' : 'Guardar'} Hecho
              {selectedFiles.length > 0 && ` y Subir ${selectedFiles.length} Doc(s)`}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export default EventoForm;
